import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

export const ADMIN_LANGUAGE_STORAGE_KEY = 'admin_language';
export const SUPPORTED_ADMIN_LANGUAGES = ['zh', 'en'];

export const adminTranslations = {
  zh: {
    layout: {
      product: 'Oblitrok 管理',
      title: '管理后台',
      visitClient: '访问前台',
      collapse: '收起',
      expand: '展开',
      logout: '退出登录',
      language: '语言',
      groups: {
        overview: '概览',
        management: '管理',
        finance: '财务',
        system: '系统',
      },
      nav: {
        dashboard: '仪表盘',
        analytics: '数据分析',
        users: '用户管理',
        sellers: '卖家管理',
        tasks: '任务管理',
        bids: '报价管理',
        submissions: '投稿管理',
        marketplace: '服务管理',
        services: '服务管理',
        reviews: '评价管理',
        reports: '举报处理',
        posts: '帖子管理',
        categories: '分类管理',
        announcements: '公告管理',
        points: '积分管理',
        finance: '财务概览',
        wallets: '钱包管理',
        orders: '订单管理',
        disputes: '订单争议',
        refunds: '退款管理',
        checkoutRequests: '托管申请',
        withdrawals: '提现审核',
        supportMessages: '客服消息',
        settings: '系统设置',
      },
    },
    login: {
      title: '管理后台',
      subtitle: 'Oblitrok · Admin',
      email: '邮箱',
      password: '密码',
      passwordPlaceholder: '请输入密码',
      login: '登录',
      loading: '登录中...',
      noAdminPermission: '无管理员权限',
      failed: '登录失败',
      forcePasswordTitle: '请先修改初始密码',
      forcePasswordDescription: '为了保证后台安全，首次登录必须设置新的管理员密码。',
      newPassword: '新密码',
      confirmNewPassword: '确认新密码',
      confirmChangePassword: '确认修改并进入后台',
      changingPassword: '修改中...',
      passwordMismatch: '两次输入的新密码不一致',
      passwordChangeFailed: '密码修改失败',
      localDefaultEmail: '本地默认管理员邮箱',
    },
    notFound: {
      title: '页面不存在',
      description: '您访问的管理页面不存在或无权访问',
      back: '返回数据概览',
    },
    dialog: {
      confirmTitle: '确认操作',
      promptTitle: '填写备注',
      confirm: '确认',
      cancel: '取消',
    },
    dashboard: {
      title: '数据概览',
      loading: '加载中...',
      empty: '暂无数据',
      stats: {
        users: '注册用户',
        tasks: '发布任务',
        posts: '论坛帖子',
        avgRating: '平均评分',
        bids: '报价总数',
        replies: '回复总数',
        totalUsers: '总用户数',
        activeSellers: '活跃卖家',
        currentMonthGmv: '本月完成 GMV',
        pendingTodos: '待办总数',
      },
      sub: {
        newThisWeek: value => `本周新增 +${value}`,
        openTasks: value => `进行中 ${value}`,
        reviews: value => `共 ${value} 条评价`,
        sellers: value => `${value} 个卖家`,
        todo: value => `${value} 项待处理`,
      },
      charts: {
        users14d: '近14天新增用户',
        tasks14d: '近14天新增任务',
        revenue12Months: '近12个月平台营收',
        categoryRanking: '分类成交额排行',
        topSellers: '顶级卖家',
        recentOrders: '最近订单',
      },
      todos: {
        title: '待办清单',
        sellerVerification: '卖家认证',
        reportedServices: '被举报服务',
        refunds: '退款申请',
        withdrawals: '提现审核',
        view: '查看',
      },
      quickActionsTitle: '快捷操作',
      quickActions: {
        users: '管理用户',
        sellers: '管理卖家',
        tasks: '管理任务',
        posts: '帖子审核',
        announcements: '发布公告',
        categories: '板块管理',
      },
    },
    announcements: {
      title: '公告管理',
      cardTitle: '发布全站公告',
      description: '公告将以系统消息形式推送给所有注册用户',
      titleLabel: '公告标题 *',
      titlePlaceholder: '例如：平台维护通知',
      contentLabel: '公告内容',
      contentPlaceholder: '详细内容...',
      sending: '发送中...',
      submit: '发布公告',
      failed: '发送失败',
    },
    categories: {
      title: '服务分类管理',
      create: '新建服务分类',
      edit: '编辑',
      delete: '删除',
      columns: {
        icon: '图标',
        name: '名称',
        description: '描述',
        subcategories: '子类目',
        services: '服务数',
        status: '状态',
        sort: '排序',
        actions: '操作',
      },
      status: {
        active: '正常',
      },
      modal: {
        createTitle: '新建服务分类',
        editTitle: '编辑服务分类',
      },
      iconLabel: '图标',
      nameLabel: '分类名称 *',
      descriptionLabel: '分类描述',
      itemsLabel: '子类目（用逗号分隔）',
      itemsPlaceholder: '例如：网站制作、自动化脚本、数据整理',
      namePlaceholder: '例如：技术服务',
      descriptionPlaceholder: '简短描述...',
      statusInactive: '停用',
      cancel: '取消',
      saving: '保存中...',
      save: '保存',
      deleteConfirm: {
        title: '删除服务分类',
        message: '确定删除该分类？仅可删除没有关联服务的分类。',
      },
    },
    tasks: {
      title: '任务管理',
      searchPlaceholder: '搜索任务标题...',
      allStatus: '全部状态',
      total: value => `共 ${value} 个任务`,
      loading: '加载中...',
      previous: '上一页',
      next: '下一页',
      page: value => `第 ${value} 页`,
      status: {
        open: '招募中',
        in_progress: '进行中',
        completed: '已完成',
        closed: '已关闭',
      },
      columns: {
        id: 'ID',
        title: '标题',
        category: '分类',
        owner: '发布者',
        budget: '预算',
        bids: '报价数',
        status: '状态',
        createdAt: '发布时间',
        actions: '操作',
      },
      actions: {
        close: '关闭',
        restore: '恢复',
        delete: '删除',
      },
      deleteConfirm: {
        title: '删除任务',
        message: '确定删除该任务？',
      },
    },
    bids: {
      title: '报价管理',
      searchPlaceholder: '搜索任务或报价人...',
      allStatus: '全部状态',
      loading: '加载中...',
      empty: '暂无数据',
      showing: (filtered, total) => `显示 ${filtered} / ${total} 条`,
      status: {
        pending: '待处理',
        accepted: '已接受',
        rejected: '已拒绝',
      },
      stats: {
        total: '总报价',
        pending: '待处理',
        accepted: '已接受',
        rejected: '已拒绝',
      },
      columns: {
        id: 'ID',
        task: '任务',
        bidder: '报价人',
        price: '金额',
        message: '留言',
        status: '状态',
        createdAt: '时间',
        actions: '操作',
      },
      actions: {
        accept: '接受',
        reject: '拒绝',
        delete: '删除',
      },
      confirm: {
        accept: { title: '接受报价', message: '确认接受此报价？' },
        reject: { title: '拒绝报价', message: '确认拒绝此报价？' },
        delete: { title: '删除报价', message: '确认删除此报价？' },
      },
      errors: {
        loadFailed: '报价数据加载失败',
        acceptFailed: '接受报价失败',
        rejectFailed: '拒绝报价失败',
        deleteFailed: '删除报价失败',
      },
    },
    submissions: {
      title: '投稿管理',
      searchPlaceholder: '搜索任务、提交人或内容...',
      allStatus: '全部状态',
      loading: '加载中...',
      empty: '暂无数据',
      showing: (filtered, total) => `显示 ${filtered} / ${total} 条`,
      attachmentsPrefix: '附件',
      status: {
        pending: '待审核',
        approved: '已通过',
        rejected: '已拒绝',
      },
      columns: {
        id: 'ID',
        task: '任务',
        submitter: '提交人',
        content: '内容',
        attachments: '附件',
        status: '状态',
        createdAt: '时间',
        actions: '操作',
      },
      actions: {
        approve: '通过',
        reject: '拒绝',
        delete: '删除',
      },
      confirm: {
        approve: { title: '通过投稿', message: '通过此投稿？' },
        reject: { title: '拒绝投稿', message: '拒绝此投稿？' },
        delete: { title: '删除投稿', message: '删除此投稿？不可恢复！' },
      },
      errors: {
        loadFailed: '投稿数据加载失败',
      },
    },
    posts: {
      title: '帖子管理',
      searchPlaceholder: '搜索帖子标题...',
      total: value => `共 ${value} 个帖子`,
      loading: '加载中...',
      previous: '上一页',
      next: '下一页',
      page: value => `第 ${value} 页`,
      pinnedBadge: '置顶',
      columns: {
        id: 'ID',
        title: '标题',
        category: '板块',
        author: '作者',
        views: '浏览',
        replies: '回复',
        pinned: '置顶',
        createdAt: '发布时间',
        actions: '操作',
      },
      actions: {
        pin: '置顶',
        unpin: '取消置顶',
        delete: '删除',
      },
      deleteConfirm: {
        title: '删除帖子',
        message: '确定删除该帖子及所有回复？',
      },
    },
    reviews: {
      title: '评价管理',
      searchPlaceholder: '搜索评论内容或用户名...',
      loading: '加载中...',
      empty: '暂无数据',
      showing: value => `显示 ${value} 条`,
      stats: {
        total: '总评价数',
        average: '平均分',
        fiveStar: '5 星',
        sensitive: '疑似违规',
      },
      ratingFilter: {
        all: '全部评分',
        five: '5 星',
        four: '4 星',
        three: '3 星',
        two: '2 星',
        one: '1 星',
        sensitive: '仅看敏感词',
      },
      columns: {
        id: 'ID',
        task: '任务',
        reviewer: '评价者',
        reviewee: '被评价',
        rating: '评分',
        comment: '评论内容',
        createdAt: '时间',
        actions: '操作',
      },
      actions: {
        delete: '删除',
      },
      deleteConfirm: {
        title: '删除评价',
        message: '确定删除此评价？此操作不可恢复',
      },
      errors: {
        loadFailed: '评价数据加载失败',
      },
    },
    reports: {
      title: '举报处理',
      searchPlaceholder: '搜索标题、举报人、原因...',
      allStatus: '全部状态',
      allTypes: '全部类型',
      loading: '加载中...',
      empty: '暂无举报',
      showing: value => `显示 ${value} 条`,
      adminNote: '处理备注',
      adminNoteAria: id => `处理备注 ${id}`,
      status: {
        pending: '待处理',
        reviewing: '处理中',
        resolved: '已处理',
        rejected: '已驳回',
      },
      type: {
        service: '技能服务',
        product: '二手商品',
      },
      stats: {
        total: '全部举报',
        pending: '待处理',
        reviewing: '处理中',
        resolved: '已处理',
      },
      columns: {
        id: 'ID',
        type: '类型',
        target: '举报对象',
        reporter: '举报人',
        reason: '原因',
        detail: '说明',
        status: '状态',
        adminNote: '处理备注',
        createdAt: '时间',
        actions: '操作',
      },
      actions: {
        reviewing: '处理中',
        resolved: '已处理',
        rejected: '驳回',
      },
      errors: {
        loadFailed: '举报数据加载失败',
        updateFailed: '举报状态更新失败',
      },
    },
    supportMessages: {
      title: '客服消息',
      inboxTitle: '客服收件箱',
      conversationCount: value => `${value} 个会话`,
      searchPlaceholder: '搜索用户或消息...',
      loading: '加载中...',
      loadingMessages: '消息加载中...',
      empty: '暂无客服会话',
      noMessages: '暂无消息',
      selectConversation: '选择一个客服会话开始处理',
      supportTag: '平台客服',
      userFallback: id => `用户 #${id || '-'}`,
      replyPlaceholder: '输入回复，按 Enter 发送，Shift+Enter 换行',
      send: '发送',
      sending: '发送中...',
      errors: {
        loadFailed: '客服会话加载失败',
        messagesFailed: '客服消息加载失败',
        sendFailed: '客服消息发送失败',
      },
    },
    marketplaceReview: {
      title: '市场内容审核',
      allStatus: '全部状态',
      loading: '加载中...',
      empty: '暂无数据',
      showing: value => `显示 ${value} 条`,
      tabs: {
        services: '技能服务',
        products: '二手商品',
      },
      status: {
        pending: '待审核',
        reported: '被举报',
        published: '已发布',
        rejected: '已拒绝',
      },
      columns: {
        id: 'ID',
        title: '标题',
        category: '分类',
        publisher: '发布人',
        priceMethod: '价格/方式',
        reviews: '评价数',
        description: '说明',
        status: '状态',
        createdAt: '时间',
        actions: '操作',
      },
      actions: {
        publish: '发布',
        reject: '拒绝',
        returnPending: '退回待审',
        delete: '删除',
        retry: '重试',
      },
      deleteConfirm: {
        title: '删除市场内容',
        message: '确认删除？此操作不可恢复。',
      },
      errors: {
        loadFailed: '市场内容加载失败',
      },
    },
    points: {
      title: '积分管理',
      loading: '加载中...',
      searchPlaceholder: '搜索用户...',
      userCount: value => `${value} 位`,
      recentLogs: '最近日志',
      levels: {
        expert: '大神',
        pro: '达人',
        active: '活跃',
        new: '新人',
      },
      stats: {
        users: '用户数',
        totalPoints: '积分总量',
        logs: '积分日志',
      },
      columns: {
        rank: '排名',
        user: '用户',
        email: '邮箱',
        points: '积分',
        level: '等级',
        actions: '操作',
      },
      actions: {
        adjust: '调整',
        cancel: '取消',
        confirm: '确认',
      },
      adjustModal: {
        title: username => `调整 ${username} 的积分`,
        changeLabel: '变化值（正数增加，负数减少）',
        changePlaceholder: '例如 +10 或 -5',
        reasonLabel: '原因',
        reasonPlaceholder: '例如：任务完成奖励',
      },
      errors: {
        loadFailed: '积分数据加载失败',
        requiredFields: '请填写所有字段',
        adjustFailed: '积分调整失败',
      },
    },
    financeDashboard: {
      title: '财务概览',
      loading: '加载中...',
      empty: '暂无数据',
      errorPrefix: '加载失败',
      requestUnit: '笔',
      orderUnit: () => '单',
      cards: {
        totalBalance: '平台总余额',
        totalFrozen: '托管中金额',
        todayRecharge: '今日充值',
        todayOrders: '今日订单',
        escrowOrders: '托管订单',
        completedOrders: '已完成订单',
        refundedOrders: '已退款订单',
        pendingWithdrawals: '待审核提现',
        pendingCheckoutRequests: '待处理托管申请',
      },
      trends: {
        recharge14d: '近 14 天充值走势',
        orders14d: '近 14 天订单走势',
      },
    },
    users: {
      title: '用户管理',
      searchPlaceholder: '搜索用户名 / 邮箱...',
      total: value => `共 ${value} 名用户`,
      loading: '加载中...',
      previous: '上一页',
      next: '下一页',
      page: value => `第 ${value} 页`,
      emptyValue: '-',
      roles: {
        admin: '管理员',
        user: '普通用户',
        banned: '已封禁',
      },
      adminRoles: {
        super_admin: '超级管理员',
        operations: '运营',
        finance: '财务',
        content: '内容审核',
        readonly: '只读',
      },
      verification: {
        verified: '已认证',
        unverified: '未认证',
      },
      columns: {
        id: 'ID',
        username: '用户名',
        email: '邮箱',
        region: '地区',
        role: '角色',
        marketplaceRole: '平台角色',
        adminRole: '后台权限',
        verification: '认证',
        points: '积分',
        tasks: '任务数',
        posts: '帖子数',
        orders: '订单数',
        spending: '消费额',
        createdAt: '注册时间',
        actions: '操作',
      },
      marketplaceRoles: {
        admin: '管理员',
        buyer: '买家',
        seller: '卖家',
        both: '买家 / 卖家',
      },
      actions: {
        ban: '封禁',
        unban: '解封',
        demote: '降权',
        setAdmin: '设管理员',
        setVerified: '设为已认证',
        unverify: '取消认证',
        delete: '删除',
      },
      confirm: {
        ban: {
          title: action => `${action}用户`,
          message: action => `确定${action}该用户吗？`,
        },
        role: {
          title: '修改用户角色',
          message: role => `确定将该用户设为${role}吗？`,
        },
        adminRole: {
          title: '修改后台权限',
          message: '确定修改该管理员后台权限吗？',
        },
        verification: {
          title: '修改认证状态',
          message: action => `确定${action}吗？`,
        },
        delete: {
          title: '删除用户',
          message: '确定删除该用户？此操作不可恢复！',
        },
      },
    },
    sellers: {
      title: '卖家管理',
      searchPlaceholder: '搜索卖家用户名 / 邮箱...',
      statusFilter: '卖家状态筛选',
      total: value => `共 ${value} 位卖家`,
      loading: '加载中...',
      empty: '暂无卖家数据',
      previous: '上一页',
      next: '下一页',
      page: value => `第 ${value} 页`,
      status: {
        all: '全部状态',
        active: '正常',
        pending: '待认证',
        suspended: '已封禁',
      },
      verification: {
        verified: '已认证',
        unverified: '未认证',
      },
      level: {
        deferred: '暂缓',
      },
      columns: {
        seller: '卖家',
        level: '等级',
        rating: '评分',
        orders: '完成订单',
        settlement: '累计结算',
        services: '服务数',
        verification: '认证',
        account: '账号状态',
        actions: '操作',
      },
      actions: {
        verify: '通过认证',
        reject: '取消认证',
        suspend: '封禁',
        unsuspend: '解封',
        retry: '重试',
      },
      confirm: {
        verification: {
          title: '修改卖家认证',
          message: (action, username) => `确定对卖家「${username}」执行${action}吗？`,
        },
        suspension: {
          title: '修改卖家账号状态',
          message: (action, username) => `确定对卖家「${username}」执行${action}吗？`,
        },
      },
      errors: {
        loadFailed: '卖家数据加载失败',
      },
    },
    wallets: {
      title: '钱包管理',
      loading: '加载中...',
      empty: '暂无用户',
      emptyValue: '-',
      searchPlaceholder: '搜索用户名 / 邮箱...',
      search: '搜索',
      stats: {
        users: '用户数',
        totalBalance: '总余额',
        totalFrozen: '托管中',
      },
      columns: {
        id: 'ID',
        user: '用户',
        email: '邮箱',
        balance: '可用余额',
        frozen: '托管中',
        points: '积分',
        createdAt: '注册时间',
        actions: '操作',
      },
      actions: {
        adjust: '调账',
        cancel: '取消',
        confirm: '确认调账',
        processing: '处理中...',
      },
      adjustModal: {
        title: username => `调整 ${username} 的余额`,
        currentBalance: '当前余额',
        amountLabel: '调整金额（元，正数增，负数减）',
        amountPlaceholder: '例如 100 或 -50',
        reasonLabel: '调整原因（必填，会写入审计流水）',
        reasonPlaceholder: '例如：活动奖励',
      },
      errors: {
        requiredFields: '请填写金额和原因',
      },
    },
    orders: {
      title: '订单管理',
      loading: '加载中...',
      empty: '暂无订单',
      emptyValue: '-',
      total: value => `共 ${value} 条`,
      searchPlaceholder: '搜索订单号 / 任务 / 用户名...',
      search: '搜索',
      status: {
        pending_payment: '待托管付款',
        escrow: '托管中',
        completed: '已完成',
        refunded: '已退款',
        cancelled: '已取消',
        disputed: '争议中',
      },
      filters: {
        allStatus: '全部状态',
      },
      columns: {
        orderNo: '订单号',
        task: '任务',
        buyer: '买家',
        seller: '卖家',
        amount: '金额',
        platformFee: '平台抽成',
        status: '状态',
        createdAt: '创建时间',
        actions: '操作',
      },
      actions: {
        forceRefund: '强制退款',
      },
      forceRefund: {
        promptTitle: orderNo => `强制退款 ${orderNo}`,
        promptMessage: '请填写原因（会写入审计流水）：',
        promptPlaceholder: '请输入退款原因',
        confirmTitle: '确认强制退款',
        confirmMessage: (amount, buyerName) => `确认将 ¥${amount} 退回买家 ${buyerName}？此操作不可撤销。`,
      },
    },
    checkoutRequests: {
      title: '托管付款申请',
      loading: '加载中...',
      empty: '暂无托管付款申请',
      emptyValue: '-',
      summary: (count, amount) => `共 ${count} 条，合计 ¥ ${amount}`,
      status: {
        pending: '待处理',
        verifying: '待核验付款',
        processing: '处理中',
        paid: '已支付',
        completed: '已完成',
        cancelled: '已取消',
      },
      filters: {
        allStatus: '全部状态',
      },
      columns: {
        id: 'ID',
        applicant: '申请人',
        project: '项目',
        amount: '金额',
        description: '说明',
        paymentInfo: '付款信息',
        paymentProof: '付款凭证',
        status: '状态',
        createdAt: '创建时间',
        actions: '操作',
      },
      actions: {
        confirmPaid: '确认到账',
        markProcessing: '标记处理中',
        complete: '完成',
        cancel: '取消',
      },
      labels: {
        method: '方式',
        reference: '交易号',
        payer: '付款人',
        paymentTime: '付款时间',
        submittedAt: '提交时间',
        verifiedAt: '核验时间',
        adminNote: '备注',
      },
      confirmPaid: {
        title: '确认到账',
        message: title => `确认「${title}」已到账？可填写核验备注：`,
        placeholder: '如：已核对银行流水 / Wise 到账',
      },
      confirm: {
        title: '更新托管申请状态',
        message: (title, status) => `确认将「${title}」更新为“${status}”？`,
      },
      errors: {
        loadFailed: '托管付款申请加载失败',
      },
    },
    withdrawals: {
      title: '提现审核',
      loading: '加载中...',
      empty: '暂无提现申请',
      emptyValue: '-',
      summary: (count, amount) => `共 ${count} 笔，合计 ¥ ${amount}`,
      status: {
        pending: '待审核',
        approved: '已通过',
        rejected: '已驳回',
        all: '全部',
        paid: '已打款',
        handled: '已处理',
      },
      accountTypes: {
        alipay: '支付宝',
        bank: '银行卡',
      },
      columns: {
        id: 'ID',
        user: '用户',
        amount: '金额',
        account: '收款账户',
        status: '状态',
        createdAt: '申请时间',
        note: '审核备注',
        actions: '操作',
      },
      actions: {
        autoPayout: '自动出款',
        approve: '手动通过',
        reject: '驳回',
        viewQr: '查看收款码',
      },
      autoPayout: {
        promptTitle: '自动出款',
        promptMessage: (amount, username) => `自动出款 ¥${amount} 给 ${username}？备注（可选）：`,
        promptPlaceholder: '填写出款备注，可留空',
        confirmTitle: '确认自动出款',
        confirmMessage: '系统会调用支付宝转账接口，成功后提现单会自动标记为已打款。确认继续？',
      },
      approve: {
        promptTitle: '手动通过提现',
        promptMessage: (amount, username) => `手动通过 ¥${amount} 提现给 ${username}？备注（可选）：`,
        promptPlaceholder: '填写审核备注，可留空',
        confirmTitle: '确认手动通过提现',
        confirmMessage: '确认通过提现？请确保已线下打款。',
      },
      reject: {
        promptTitle: '驳回提现',
        promptMessage: '请填写驳回原因（必填）：',
        promptPlaceholder: '请输入驳回原因',
        confirmTitle: '确认驳回提现',
        confirmMessage: amount => `驳回后 ¥${amount} 将原路退回到用户余额。确认？`,
      },
    },
    disputes: {
      title: '订单争议',
      loading: '加载中...',
      empty: '暂无订单争议',
      emptyValue: '-',
      summary: (count, amount) => `共 ${count} 条，合计 ¥ ${amount}`,
      taskFallback: id => `任务 #${id || '-'}`,
      buyerLabel: '买家',
      sellerLabel: '卖家',
      notePlaceholder: '处理说明',
      noteRequired: id => `请先填写 #${id} 的处理说明`,
      resolvedMessage: (id, resolution) => `#${id} 已${resolution}`,
      status: {
        open: '处理中',
        refunded: '已退款',
        released: '已放款',
        closed: '已关闭',
        all: '全部',
      },
      resolution: {
        refund: '退款',
        release: '放款',
        close: '关闭',
      },
      columns: {
        id: 'ID',
        order: '订单',
        parties: '买卖双方',
        amount: '金额',
        reason: '争议说明',
        evidence: '证据',
        status: '状态',
        handling: '处理',
      },
      errors: {
        loadFailed: '争议列表加载失败',
        resolveFailed: '争议处理失败',
      },
    },
    settings: {
      title: '后台设置',
      loading: '加载中...',
      save: '保存',
      saved: '设置已保存',
      defaultConfig: {
        siteDesc: '专业技能交易平台',
        welcomeEmailTemplate: '欢迎加入 {{siteName}}。你可以先完善资料，再开始发布需求、承接任务或查看通知。',
        passwordResetTemplate: '我们收到了重置 {{siteName}} 账号密码的请求。请点击下面的链接继续操作：{{resetUrl}}。如果不是你本人发起，请忽略这封邮件。',
        notificationDigestTemplate: '这是你在 {{siteName}} 的消息摘要。登录后可以查看新消息、订单和待办事项。',
        smsTestMessage: '短信测试：{{siteName}} 后台短信通道验证',
      },
      tabs: {
        general: '⚙️ 基本信息',
        policy: '🔐 注册策略',
        finance: '💳 财务分成',
        email: '📧 邮件、短信与密码',
        notifications: '🔔 通知模板',
        system: '🛠 系统维护',
      },
      general: {
        title: '基本信息',
        siteName: '站点名称',
        siteDesc: '站点描述',
        contactEmail: '联系邮箱',
      },
      policy: {
        title: '注册策略',
        regEnabledTitle: '开放注册',
        regEnabledDesc: '允许新用户注册账户',
        guestBrowseTitle: '游客浏览',
        guestBrowseDesc: '允许未登录用户浏览公开内容',
        autoPointsTitle: '自动积分',
        autoPointsDesc: '发帖/回帖/完成任务自动获得积分',
      },
      finance: {
        title: '平台交易规则',
        description: '配置订单完成后的平台抽成、技术员结算比例、托管结算周期和提现门槛。已创建的托管订单会保留创建时的分成快照。',
        platformFeeRate: '平台服务费率（%）',
        providerShareRate: '技术员结算比例（%）',
        escrowReleaseDays: '托管结算周期（天）',
        minWithdrawalAmount: '最低提现金额（元）',
        supportEmail: '客服邮箱',
        emailNotifTitle: '邮件通知',
        emailNotifDesc: '开启后，订单、退款、提现等关键通知会通过邮件发送。',
        example: (platformFeeRate, providerShareRate) => `例如 100 元订单：平台服务费 ${platformFeeRate} 元，技术员结算 ${providerShareRate} 元。`,
      },
      email: {
        title: '邮件、短信与密码重置',
        description: '管理系统发送的通知模板，并处理用户提交的密码重置请求。',
        emailNotifTitle: '启用邮件通知',
        emailNotifDesc: '给用户发送邮件提醒',
        welcomeTemplate: '欢迎邮件模板',
        passwordResetTemplate: '密码重置模板',
        notificationDigestTemplate: '通知摘要模板',
        emailTestTitle: '邮件测试',
        emailStatus: (provider, configured) => `Provider：${provider} · 通道：${configured ? '已配置' : '未配置'}`,
        emailStatusNotLoaded: '邮件通道状态未加载',
        emailSubmitted: '邮件测试已提交',
        emailFailed: '邮件测试失败',
        deliveryLogsTitle: '最近邮件投递记录',
        deliveryLogsEmpty: '暂无邮件投递记录',
        deliveryLogMeta: (templateKey, provider, createdAt) => `模板：${templateKey} · Provider：${provider} · ${createdAt}`,
        smsTestTitle: '短信测试',
        smsStatus: (provider, twilioConfigured) => `Provider：${provider} · Twilio：${twilioConfigured ? '已配置' : '未配置'}`,
        smsStatusNotLoaded: '短信通道状态未加载',
        refreshStatus: '刷新状态',
        refresh: '刷新',
        loading: '加载中...',
        sending: '发送中...',
        sendTest: '发送测试',
        smsSubmitted: '短信测试已提交',
        smsFailed: '短信测试失败',
        resetRequestsTitle: '密码重置请求',
        resetEmpty: '暂无重置请求',
        accountStatus: (username, status, createdAt) => `账号：${username} · 状态：${status} · ${createdAt}`,
        unmatchedAccount: '未匹配',
        resolved: '已处理',
        ignored: '忽略',
        ignoredSaved: '已忽略重置请求',
        resolvedSaved: '已标记为已处理',
      },
      notifications: {
        title: '站内通知模板',
        description: '编辑系统自动通知的中英文标题和正文。变量请保留双花括号格式，例如 {{username}}。',
        loading: '加载中...',
        count: count => `共 ${count} 个模板`,
        refresh: '刷新',
        empty: '暂无可编辑模板',
        variableList: variables => `变量：${(variables || []).map(v => `{{${v}}}`).join('、') || '无'}`,
        overridden: '已覆盖',
        zhTitle: '中文标题',
        zhContent: '中文正文',
        enTitle: '英文标题',
        enContent: '英文正文',
        save: '保存通知模板',
        saved: '通知模板已保存',
      },
      system: {
        title: '系统维护',
        maintenanceMode: '维护模式',
        maintenanceModeDesc: '开启后普通用户无法访问，仅管理员可用',
        backupTitle: '数据备份',
        generating: '生成中...',
        downloadDatabase: '下载数据库',
        exporting: '导出中...',
        exportLogs: '导出日志',
        processing: '处理中...',
        clearCache: '清理缓存',
        backupGenerated: '数据备份已生成',
        logsExported: '审计日志已导出',
        cacheRefreshed: '缓存已刷新',
        dangerTitle: '⚠️ 危险操作',
        dangerDescription: '生产环境不提供页面一键清库。需要执行数据重置时，请先下载备份，再由管理员在服务器侧执行受控脚本。',
        saveConfig: '保存配置',
      },
    },
  },
  en: {
    layout: {
      product: 'Oblitrok Admin',
      title: 'Admin Console',
      visitClient: 'Open Client',
      collapse: 'Collapse',
      expand: 'Expand',
      logout: 'Log out',
      language: 'Language',
      groups: {
        overview: 'Overview',
        management: 'Management',
        finance: 'Finance',
        system: 'System',
      },
      nav: {
        dashboard: 'Dashboard',
        analytics: 'Analytics',
        users: 'Users',
        sellers: 'Sellers',
        tasks: 'Tasks',
        bids: 'Bids',
        submissions: 'Submissions',
        marketplace: 'Services',
        services: 'Services',
        reviews: 'Reviews',
        reports: 'Reports',
        posts: 'Posts',
        categories: 'Categories',
        announcements: 'Announcements',
        points: 'Points',
        finance: 'Finance',
        wallets: 'Wallets',
        orders: 'Orders',
        disputes: 'Disputes',
        refunds: 'Refunds',
        checkoutRequests: 'Checkout Requests',
        withdrawals: 'Withdrawals',
        supportMessages: 'Support messages',
        settings: 'Settings',
      },
    },
    login: {
      title: 'Admin Console',
      subtitle: 'Oblitrok · Admin',
      email: 'Email',
      password: 'Password',
      passwordPlaceholder: 'Enter password',
      login: 'Log in',
      loading: 'Logging in...',
      noAdminPermission: 'No admin permission',
      failed: 'Login failed',
      forcePasswordTitle: 'Change the initial password',
      forcePasswordDescription: 'For admin security, set a new password before entering the console.',
      newPassword: 'New password',
      confirmNewPassword: 'Confirm new password',
      confirmChangePassword: 'Change password and continue',
      changingPassword: 'Changing...',
      passwordMismatch: 'The two new passwords do not match',
      passwordChangeFailed: 'Password change failed',
      localDefaultEmail: 'Local default admin email',
    },
    notFound: {
      title: 'Page not found',
      description: 'The admin page does not exist or you do not have access.',
      back: 'Back to dashboard',
    },
    dialog: {
      confirmTitle: 'Confirm action',
      promptTitle: 'Add note',
      confirm: 'Confirm',
      cancel: 'Cancel',
    },
    dashboard: {
      title: 'Dashboard',
      loading: 'Loading...',
      empty: 'No data',
      stats: {
        users: 'Registered users',
        tasks: 'Published tasks',
        posts: 'Forum posts',
        avgRating: 'Average rating',
        bids: 'Total bids',
        replies: 'Total replies',
        totalUsers: 'Total users',
        activeSellers: 'Active sellers',
        currentMonthGmv: 'Completed GMV this month',
        pendingTodos: 'Pending todos',
      },
      sub: {
        newThisWeek: value => `+${value} this week`,
        openTasks: value => `${value} in progress`,
        reviews: value => `${value} reviews`,
        sellers: value => `${value} sellers`,
        todo: value => `${value} pending`,
      },
      charts: {
        users14d: 'New users in 14 days',
        tasks14d: 'New tasks in 14 days',
        revenue12Months: 'Platform revenue in 12 months',
        categoryRanking: 'GMV by category',
        topSellers: 'Top sellers',
        recentOrders: 'Recent orders',
      },
      todos: {
        title: 'Pending work',
        sellerVerification: 'Seller verification',
        reportedServices: 'Reported services',
        refunds: 'Refund requests',
        withdrawals: 'Withdrawal reviews',
        view: 'View',
      },
      quickActionsTitle: 'Quick actions',
      quickActions: {
        users: 'Manage users',
        sellers: 'Manage sellers',
        tasks: 'Manage tasks',
        posts: 'Review posts',
        announcements: 'Publish announcement',
        categories: 'Manage categories',
      },
    },
    announcements: {
      title: 'Announcements',
      cardTitle: 'Publish site-wide announcement',
      description: 'Announcements are pushed to all registered users as system messages.',
      titleLabel: 'Announcement title *',
      titlePlaceholder: 'Example: Platform maintenance notice',
      contentLabel: 'Announcement content',
      contentPlaceholder: 'Detailed content...',
      sending: 'Sending...',
      submit: 'Publish announcement',
      failed: 'Send failed',
    },
    categories: {
      title: 'Service categories',
      create: 'New service category',
      edit: 'Edit',
      delete: 'Delete',
      columns: {
        icon: 'Icon',
        name: 'Name',
        description: 'Description',
        subcategories: 'Subcategories',
        services: 'Services',
        status: 'Status',
        sort: 'Sort',
        actions: 'Actions',
      },
      status: {
        active: 'Active',
      },
      modal: {
        createTitle: 'New service category',
        editTitle: 'Edit service category',
      },
      iconLabel: 'Icon',
      nameLabel: 'Category name *',
      descriptionLabel: 'Category description',
      itemsLabel: 'Subcategories (comma separated)',
      itemsPlaceholder: 'Example: Websites, automation, data cleanup',
      namePlaceholder: 'Example: Development',
      descriptionPlaceholder: 'Short description...',
      statusInactive: 'Inactive',
      cancel: 'Cancel',
      saving: 'Saving...',
      save: 'Save',
      deleteConfirm: {
        title: 'Delete service category',
        message: 'Delete this category? Categories with services cannot be deleted.',
      },
    },
    tasks: {
      title: 'Tasks',
      searchPlaceholder: 'Search task title...',
      allStatus: 'All statuses',
      total: value => `${value} tasks`,
      loading: 'Loading...',
      previous: 'Previous',
      next: 'Next',
      page: value => `Page ${value}`,
      status: {
        open: 'Open',
        in_progress: 'In progress',
        completed: 'Completed',
        closed: 'Closed',
      },
      columns: {
        id: 'ID',
        title: 'Title',
        category: 'Category',
        owner: 'Owner',
        budget: 'Budget',
        bids: 'Bids',
        status: 'Status',
        createdAt: 'Created',
        actions: 'Actions',
      },
      actions: {
        close: 'Close',
        restore: 'Restore',
        delete: 'Delete',
      },
      deleteConfirm: {
        title: 'Delete task',
        message: 'Delete this task?',
      },
    },
    bids: {
      title: 'Bids',
      searchPlaceholder: 'Search task or bidder...',
      allStatus: 'All statuses',
      loading: 'Loading...',
      empty: 'No data',
      showing: (filtered, total) => `Showing ${filtered} / ${total}`,
      status: {
        pending: 'Pending',
        accepted: 'Accepted',
        rejected: 'Rejected',
      },
      stats: {
        total: 'Total bids',
        pending: 'Pending',
        accepted: 'Accepted',
        rejected: 'Rejected',
      },
      columns: {
        id: 'ID',
        task: 'Task',
        bidder: 'Bidder',
        price: 'Price',
        message: 'Message',
        status: 'Status',
        createdAt: 'Time',
        actions: 'Actions',
      },
      actions: {
        accept: 'Accept',
        reject: 'Reject',
        delete: 'Delete',
      },
      confirm: {
        accept: { title: 'Accept bid', message: 'Accept this bid?' },
        reject: { title: 'Reject bid', message: 'Reject this bid?' },
        delete: { title: 'Delete bid', message: 'Delete this bid?' },
      },
      errors: {
        loadFailed: 'Failed to load bids',
        acceptFailed: 'Failed to accept bid',
        rejectFailed: 'Failed to reject bid',
        deleteFailed: 'Failed to delete bid',
      },
    },
    submissions: {
      title: 'Submissions',
      searchPlaceholder: 'Search task, submitter, or content...',
      allStatus: 'All statuses',
      loading: 'Loading...',
      empty: 'No data',
      showing: (filtered, total) => `Showing ${filtered} / ${total}`,
      attachmentsPrefix: 'Attachment',
      status: {
        pending: 'Pending',
        approved: 'Approved',
        rejected: 'Rejected',
      },
      columns: {
        id: 'ID',
        task: 'Task',
        submitter: 'Submitter',
        content: 'Content',
        attachments: 'Attachments',
        status: 'Status',
        createdAt: 'Time',
        actions: 'Actions',
      },
      actions: {
        approve: 'Approve',
        reject: 'Reject',
        delete: 'Delete',
      },
      confirm: {
        approve: { title: 'Approve submission', message: 'Approve this submission?' },
        reject: { title: 'Reject submission', message: 'Reject this submission?' },
        delete: { title: 'Delete submission', message: 'Delete this submission? This cannot be undone.' },
      },
      errors: {
        loadFailed: 'Failed to load submissions',
      },
    },
    posts: {
      title: 'Posts',
      searchPlaceholder: 'Search post title...',
      total: value => `${value} posts`,
      loading: 'Loading...',
      previous: 'Previous',
      next: 'Next',
      page: value => `Page ${value}`,
      pinnedBadge: 'Pinned',
      columns: {
        id: 'ID',
        title: 'Title',
        category: 'Category',
        author: 'Author',
        views: 'Views',
        replies: 'Replies',
        pinned: 'Pinned',
        createdAt: 'Created',
        actions: 'Actions',
      },
      actions: {
        pin: 'Pin',
        unpin: 'Unpin',
        delete: 'Delete',
      },
      deleteConfirm: {
        title: 'Delete post',
        message: 'Delete this post and all replies?',
      },
    },
    reviews: {
      title: 'Reviews',
      searchPlaceholder: 'Search comment or user...',
      loading: 'Loading...',
      empty: 'No data',
      showing: value => `Showing ${value}`,
      stats: {
        total: 'Total reviews',
        average: 'Average rating',
        fiveStar: '5 stars',
        sensitive: 'Potential violations',
      },
      ratingFilter: {
        all: 'All ratings',
        five: '5 stars',
        four: '4 stars',
        three: '3 stars',
        two: '2 stars',
        one: '1 star',
        sensitive: 'Sensitive only',
      },
      columns: {
        id: 'ID',
        task: 'Task',
        reviewer: 'Reviewer',
        reviewee: 'Reviewee',
        rating: 'Rating',
        comment: 'Comment',
        createdAt: 'Time',
        actions: 'Actions',
      },
      actions: {
        delete: 'Delete',
      },
      deleteConfirm: {
        title: 'Delete review',
        message: 'Delete this review? This cannot be undone.',
      },
      errors: {
        loadFailed: 'Failed to load reviews',
      },
    },
    reports: {
      title: 'Reports',
      searchPlaceholder: 'Search title, reporter, or reason...',
      allStatus: 'All statuses',
      allTypes: 'All types',
      loading: 'Loading...',
      empty: 'No reports',
      showing: value => `Showing ${value}`,
      adminNote: 'Admin note',
      adminNoteAria: id => `Admin note ${id}`,
      status: {
        pending: 'Pending',
        reviewing: 'Reviewing',
        resolved: 'Resolved',
        rejected: 'Rejected',
      },
      type: {
        service: 'Service',
        product: 'Secondhand product',
      },
      stats: {
        total: 'All reports',
        pending: 'Pending',
        reviewing: 'Reviewing',
        resolved: 'Resolved',
      },
      columns: {
        id: 'ID',
        type: 'Type',
        target: 'Target',
        reporter: 'Reporter',
        reason: 'Reason',
        detail: 'Detail',
        status: 'Status',
        adminNote: 'Admin note',
        createdAt: 'Time',
        actions: 'Actions',
      },
      actions: {
        reviewing: 'Reviewing',
        resolved: 'Resolved',
        rejected: 'Reject',
      },
      errors: {
        loadFailed: 'Failed to load reports',
        updateFailed: 'Failed to update report status',
      },
    },
    supportMessages: {
      title: 'Support messages',
      inboxTitle: 'Support inbox',
      conversationCount: value => `${value} conversations`,
      searchPlaceholder: 'Search users or messages...',
      loading: 'Loading...',
      loadingMessages: 'Loading messages...',
      empty: 'No support conversations',
      noMessages: 'No messages yet',
      selectConversation: 'Select a support conversation to start handling it',
      supportTag: 'Platform support',
      userFallback: id => `User #${id || '-'}`,
      replyPlaceholder: 'Type a reply. Enter sends, Shift+Enter adds a line break',
      send: 'Send',
      sending: 'Sending...',
      errors: {
        loadFailed: 'Failed to load support conversations',
        messagesFailed: 'Failed to load support messages',
        sendFailed: 'Failed to send support message',
      },
    },
    marketplaceReview: {
      title: 'Marketplace Review',
      allStatus: 'All statuses',
      loading: 'Loading...',
      empty: 'No data',
      showing: value => `Showing ${value}`,
      tabs: {
        services: 'Services',
        products: 'Secondhand products',
      },
      status: {
        pending: 'Pending',
        reported: 'Reported',
        published: 'Published',
        rejected: 'Rejected',
      },
      columns: {
        id: 'ID',
        title: 'Title',
        category: 'Category',
        publisher: 'Publisher',
        priceMethod: 'Price / Method',
        reviews: 'Reviews',
        description: 'Description',
        status: 'Status',
        createdAt: 'Time',
        actions: 'Actions',
      },
      actions: {
        publish: 'Publish',
        reject: 'Reject',
        returnPending: 'Return to pending',
        delete: 'Delete',
        retry: 'Retry',
      },
      deleteConfirm: {
        title: 'Delete marketplace content',
        message: 'Delete this content? This cannot be undone.',
      },
      errors: {
        loadFailed: 'Failed to load marketplace content',
      },
    },
    points: {
      title: 'Points',
      loading: 'Loading...',
      searchPlaceholder: 'Search users...',
      userCount: value => `${value} users`,
      recentLogs: 'Recent logs',
      levels: {
        expert: 'Expert',
        pro: 'Pro',
        active: 'Active',
        new: 'New',
      },
      stats: {
        users: 'Users',
        totalPoints: 'Total points',
        logs: 'Point logs',
      },
      columns: {
        rank: 'Rank',
        user: 'User',
        email: 'Email',
        points: 'Points',
        level: 'Level',
        actions: 'Actions',
      },
      actions: {
        adjust: 'Adjust',
        cancel: 'Cancel',
        confirm: 'Confirm',
      },
      adjustModal: {
        title: username => `Adjust ${username}'s points`,
        changeLabel: 'Change value (positive adds, negative subtracts)',
        changePlaceholder: 'Example: +10 or -5',
        reasonLabel: 'Reason',
        reasonPlaceholder: 'Example: task completion reward',
      },
      errors: {
        loadFailed: 'Failed to load points data',
        requiredFields: 'Please fill in all fields',
        adjustFailed: 'Failed to adjust points',
      },
    },
    financeDashboard: {
      title: 'Finance',
      loading: 'Loading...',
      empty: 'No data',
      errorPrefix: 'Load failed',
      requestUnit: 'requests',
      orderUnit: value => Number(value) === 1 ? 'order' : 'orders',
      cards: {
        totalBalance: 'Total platform balance',
        totalFrozen: 'Escrowed balance',
        todayRecharge: 'Today recharge',
        todayOrders: 'Today orders',
        escrowOrders: 'Escrow orders',
        completedOrders: 'Completed orders',
        refundedOrders: 'Refunded orders',
        pendingWithdrawals: 'Pending withdrawals',
        pendingCheckoutRequests: 'Pending checkout requests',
      },
      trends: {
        recharge14d: 'Recharge trend in 14 days',
        orders14d: 'Order trend in 14 days',
      },
    },
    users: {
      title: 'Users',
      searchPlaceholder: 'Search username / email...',
      total: value => `${value} users`,
      loading: 'Loading...',
      previous: 'Previous',
      next: 'Next',
      page: value => `Page ${value}`,
      emptyValue: '-',
      roles: {
        admin: 'Admin',
        user: 'User',
        banned: 'Banned',
      },
      adminRoles: {
        super_admin: 'Super admin',
        operations: 'Operations',
        finance: 'Finance',
        content: 'Content review',
        readonly: 'Read only',
      },
      verification: {
        verified: 'Verified',
        unverified: 'Unverified',
      },
      columns: {
        id: 'ID',
        username: 'Username',
        email: 'Email',
        region: 'Region',
        role: 'Role',
        marketplaceRole: 'Marketplace role',
        adminRole: 'Admin role',
        verification: 'Verification',
        points: 'Points',
        tasks: 'Tasks',
        posts: 'Posts',
        orders: 'Orders',
        spending: 'Spend',
        createdAt: 'Registered',
        actions: 'Actions',
      },
      marketplaceRoles: {
        admin: 'Admin',
        buyer: 'Buyer',
        seller: 'Seller',
        both: 'Buyer / Seller',
      },
      actions: {
        ban: 'Ban',
        unban: 'Unban',
        demote: 'Demote',
        setAdmin: 'Set admin',
        setVerified: 'Set verified',
        unverify: 'Unverify',
        delete: 'Delete',
      },
      confirm: {
        ban: {
          title: action => `${action} user`,
          message: action => `${action} this user?`,
        },
        role: {
          title: 'Change user role',
          message: role => `Set this user as ${role}?`,
        },
        adminRole: {
          title: 'Change admin role',
          message: 'Change this admin permission group?',
        },
        verification: {
          title: 'Change verification status',
          message: action => `${action}?`,
        },
        delete: {
          title: 'Delete user',
          message: 'Delete this user? This cannot be undone.',
        },
      },
    },
    sellers: {
      title: 'Seller management',
      searchPlaceholder: 'Search seller username / email...',
      statusFilter: 'Filter seller status',
      total: value => `${value} sellers`,
      loading: 'Loading...',
      empty: 'No seller data',
      previous: 'Previous',
      next: 'Next',
      page: value => `Page ${value}`,
      status: {
        all: 'All statuses',
        active: 'Active',
        pending: 'Pending verification',
        suspended: 'Suspended',
      },
      verification: {
        verified: 'Verified',
        unverified: 'Unverified',
      },
      level: {
        deferred: 'Deferred',
      },
      columns: {
        seller: 'Seller',
        level: 'Level',
        rating: 'Rating',
        orders: 'Completed orders',
        settlement: 'Settlement',
        services: 'Services',
        verification: 'Verification',
        account: 'Account',
        actions: 'Actions',
      },
      actions: {
        verify: 'Verify',
        reject: 'Remove verification',
        suspend: 'Suspend',
        unsuspend: 'Unsuspend',
        retry: 'Retry',
      },
      confirm: {
        verification: {
          title: 'Update seller verification',
          message: (action, username) => `${action} seller "${username}"?`,
        },
        suspension: {
          title: 'Update seller account',
          message: (action, username) => `${action} seller "${username}"?`,
        },
      },
      errors: {
        loadFailed: 'Failed to load sellers',
      },
    },
    wallets: {
      title: 'Wallets',
      loading: 'Loading...',
      empty: 'No users',
      emptyValue: '-',
      searchPlaceholder: 'Search username / email...',
      search: 'Search',
      stats: {
        users: 'Users',
        totalBalance: 'Total balance',
        totalFrozen: 'Escrowed',
      },
      columns: {
        id: 'ID',
        user: 'User',
        email: 'Email',
        balance: 'Available balance',
        frozen: 'Escrowed',
        points: 'Points',
        createdAt: 'Registered',
        actions: 'Actions',
      },
      actions: {
        adjust: 'Adjust',
        cancel: 'Cancel',
        confirm: 'Confirm adjustment',
        processing: 'Processing...',
      },
      adjustModal: {
        title: username => `Adjust ${username}'s balance`,
        currentBalance: 'Current balance',
        amountLabel: 'Adjustment amount (CNY, positive adds, negative subtracts)',
        amountPlaceholder: 'Example: 100 or -50',
        reasonLabel: 'Adjustment reason (required, written to audit log)',
        reasonPlaceholder: 'Example: campaign reward',
      },
      errors: {
        requiredFields: 'Please enter an amount and reason',
      },
    },
    orders: {
      title: 'Orders',
      loading: 'Loading...',
      empty: 'No orders',
      emptyValue: '-',
      total: value => `${value} orders`,
      searchPlaceholder: 'Search order no. / task / username...',
      search: 'Search',
      status: {
        pending_payment: 'Pending escrow payment',
        escrow: 'Escrowed',
        completed: 'Completed',
        refunded: 'Refunded',
        cancelled: 'Cancelled',
        disputed: 'Disputed',
      },
      filters: {
        allStatus: 'All statuses',
      },
      columns: {
        orderNo: 'Order no.',
        task: 'Task',
        buyer: 'Buyer',
        seller: 'Seller',
        amount: 'Amount',
        platformFee: 'Platform fee',
        status: 'Status',
        createdAt: 'Created',
        actions: 'Actions',
      },
      actions: {
        forceRefund: 'Force refund',
      },
      forceRefund: {
        promptTitle: orderNo => `Force refund ${orderNo}`,
        promptMessage: 'Enter a reason. It will be written to the audit log:',
        promptPlaceholder: 'Enter refund reason',
        confirmTitle: 'Confirm force refund',
        confirmMessage: (amount, buyerName) => `Refund ¥${amount} to buyer ${buyerName}? This cannot be undone.`,
      },
    },
    checkoutRequests: {
      title: 'Checkout Requests',
      loading: 'Loading...',
      empty: 'No checkout requests',
      emptyValue: '-',
      summary: (count, amount) => `${count} requests, total ¥ ${amount}`,
      status: {
        pending: 'Pending',
        verifying: 'Verifying payment',
        processing: 'Processing',
        paid: 'Paid',
        completed: 'Completed',
        cancelled: 'Cancelled',
      },
      filters: {
        allStatus: 'All statuses',
      },
      columns: {
        id: 'ID',
        applicant: 'Applicant',
        project: 'Project',
        amount: 'Amount',
        description: 'Description',
        paymentInfo: 'Payment info',
        paymentProof: 'Payment proof',
        status: 'Status',
        createdAt: 'Created',
        actions: 'Actions',
      },
      actions: {
        confirmPaid: 'Confirm paid',
        markProcessing: 'Mark processing',
        complete: 'Complete',
        cancel: 'Cancel',
      },
      labels: {
        method: 'Method',
        reference: 'Reference',
        payer: 'Payer',
        paymentTime: 'Payment time',
        submittedAt: 'Submitted',
        verifiedAt: 'Verified',
        adminNote: 'Note',
      },
      confirmPaid: {
        title: 'Confirm payment received',
        message: title => `Confirm "${title}" as paid? Add a verification note:`,
        placeholder: 'Example: Checked bank statement / Wise received',
      },
      confirm: {
        title: 'Update checkout request status',
        message: (title, status) => `Update "${title}" to "${status}"?`,
      },
      errors: {
        loadFailed: 'Failed to load checkout requests',
      },
    },
    withdrawals: {
      title: 'Withdrawals',
      loading: 'Loading...',
      empty: 'No withdrawal requests',
      emptyValue: '-',
      summary: (count, amount) => `${count} requests, total ¥ ${amount}`,
      status: {
        pending: 'Pending',
        approved: 'Approved',
        rejected: 'Rejected',
        all: 'All',
        paid: 'Paid',
        handled: 'Handled',
      },
      accountTypes: {
        alipay: 'Alipay',
        bank: 'Bank card',
      },
      columns: {
        id: 'ID',
        user: 'User',
        amount: 'Amount',
        account: 'Receiving account',
        status: 'Status',
        createdAt: 'Requested',
        note: 'Review note',
        actions: 'Actions',
      },
      actions: {
        autoPayout: 'Auto payout',
        approve: 'Manual approve',
        reject: 'Reject',
        viewQr: 'View QR code',
      },
      autoPayout: {
        promptTitle: 'Auto payout',
        promptMessage: (amount, username) => `Automatically pay ¥${amount} to ${username}? Note (optional):`,
        promptPlaceholder: 'Enter payout note, optional',
        confirmTitle: 'Confirm auto payout',
        confirmMessage: 'The system will call the Alipay transfer API and mark the withdrawal as paid only after success. Continue?',
      },
      approve: {
        promptTitle: 'Manual withdrawal approval',
        promptMessage: (amount, username) => `Manually approve ¥${amount} withdrawal for ${username}? Note (optional):`,
        promptPlaceholder: 'Enter review note, optional',
        confirmTitle: 'Confirm manual approval',
        confirmMessage: 'Confirm approval? Make sure the offline transfer has been completed.',
      },
      reject: {
        promptTitle: 'Reject withdrawal',
        promptMessage: 'Enter rejection reason (required):',
        promptPlaceholder: 'Enter rejection reason',
        confirmTitle: 'Confirm withdrawal rejection',
        confirmMessage: amount => `Rejecting will return ¥${amount} to the user's balance. Continue?`,
      },
    },
    disputes: {
      title: 'Disputes',
      loading: 'Loading...',
      empty: 'No order disputes',
      emptyValue: '-',
      summary: (count, amount) => `${count} disputes, total ¥ ${amount}`,
      taskFallback: id => `Task #${id || '-'}`,
      buyerLabel: 'Buyer',
      sellerLabel: 'Seller',
      notePlaceholder: 'Handling note',
      noteRequired: id => `Enter a handling note for #${id} first`,
      resolvedMessage: (id, resolution) => `#${id} resolved: ${resolution}`,
      status: {
        open: 'Open',
        refunded: 'Refunded',
        released: 'Released',
        closed: 'Closed',
        all: 'All',
      },
      resolution: {
        refund: 'Refund',
        release: 'Release',
        close: 'Close',
      },
      columns: {
        id: 'ID',
        order: 'Order',
        parties: 'Parties',
        amount: 'Amount',
        reason: 'Dispute reason',
        evidence: 'Evidence',
        status: 'Status',
        handling: 'Handling',
      },
      errors: {
        loadFailed: 'Failed to load disputes',
        resolveFailed: 'Failed to resolve dispute',
      },
    },
    settings: {
      title: 'Settings',
      loading: 'Loading...',
      save: 'Save',
      saved: 'Settings saved',
      defaultConfig: {
        siteDesc: 'Professional skills marketplace',
        welcomeEmailTemplate: 'Welcome to {{siteName}}. Complete your profile first, then start posting requests, taking work, or checking updates.',
        passwordResetTemplate: 'We received a request to reset the {{siteName}} account password. Click the link below to continue: {{resetUrl}}. If you did not request this, you can ignore this email.',
        notificationDigestTemplate: 'Here is your latest {{siteName}} message digest. Sign in to review new messages, orders, and follow-ups.',
        smsTestMessage: 'SMS test: {{siteName}} admin SMS channel verification',
      },
      tabs: {
        general: '⚙️ General',
        policy: '🔐 Registration',
        finance: '💳 Finance Rules',
        email: '📧 Email, SMS and Password',
        notifications: '🔔 Notification Templates',
        system: '🛠 System Maintenance',
      },
      general: {
        title: 'General',
        siteName: 'Site name',
        siteDesc: 'Site description',
        contactEmail: 'Contact email',
      },
      policy: {
        title: 'Registration Policy',
        regEnabledTitle: 'Open registration',
        regEnabledDesc: 'Allow new users to register accounts',
        guestBrowseTitle: 'Guest browsing',
        guestBrowseDesc: 'Allow signed-out users to browse public content',
        autoPointsTitle: 'Automatic points',
        autoPointsDesc: 'Automatically grant points for posts, replies, and completed tasks',
      },
      finance: {
        title: 'Platform Transaction Rules',
        description: 'Configure platform commission, seller settlement share, escrow release timing, and minimum withdrawal amount. Existing escrow orders keep the split snapshot from creation.',
        platformFeeRate: 'Service fee rate (%)',
        providerShareRate: 'Seller settlement share (%)',
        escrowReleaseDays: 'Escrow release period (days)',
        minWithdrawalAmount: 'Minimum withdrawal amount (CNY)',
        supportEmail: 'Support email',
        emailNotifTitle: 'Email notifications',
        emailNotifDesc: 'Send key order, refund, and withdrawal notifications by email when enabled.',
        example: (platformFeeRate, providerShareRate) => `For a CNY 100 order: platform ${platformFeeRate}%, seller ${providerShareRate}%.`,
      },
      email: {
        title: 'Email, SMS and Password Reset',
        description: 'Manage notification templates and review password reset activity. Users reset passwords through email links.',
        emailNotifTitle: 'Enable email notifications',
        emailNotifDesc: 'Send email reminders to users',
        welcomeTemplate: 'Welcome email template',
        passwordResetTemplate: 'Password reset template',
        notificationDigestTemplate: 'Notification digest template',
        emailTestTitle: 'Email test',
        emailStatus: (provider, configured) => `Provider: ${provider} · Channel: ${configured ? 'Configured' : 'Not configured'}`,
        emailStatusNotLoaded: 'Email channel status not loaded',
        emailSubmitted: 'Email test submitted',
        emailFailed: 'Email test failed',
        deliveryLogsTitle: 'Recent email delivery logs',
        deliveryLogsEmpty: 'No email delivery logs',
        deliveryLogMeta: (templateKey, provider, createdAt) => `Template: ${templateKey} · Provider: ${provider} · ${createdAt}`,
        smsTestTitle: 'SMS test',
        smsStatus: (provider, twilioConfigured) => `Provider: ${provider} · Twilio: ${twilioConfigured ? 'Configured' : 'Not configured'}`,
        smsStatusNotLoaded: 'SMS channel status not loaded',
        refreshStatus: 'Refresh status',
        refresh: 'Refresh',
        loading: 'Loading...',
        sending: 'Sending...',
        sendTest: 'Send test',
        smsSubmitted: 'SMS test submitted',
        smsFailed: 'SMS test failed',
        resetRequestsTitle: 'Password reset requests',
        resetEmpty: 'No reset requests',
        accountStatus: (username, status, createdAt) => `Account: ${username} · Status: ${status} · ${createdAt}`,
        unmatchedAccount: 'Unmatched',
        resolved: 'Resolved',
        ignored: 'Ignore',
        ignoredSaved: 'Reset request ignored',
        resolvedSaved: 'Marked as resolved',
      },
      notifications: {
        title: 'In-App Notification Templates',
        description: 'Edit the Chinese and English titles and bodies for automatic system notifications. Keep variables in double braces, for example {{username}}.',
        loading: 'Loading...',
        count: count => `${count} templates`,
        refresh: 'Refresh',
        empty: 'No editable templates',
        variableList: variables => `Variables: ${(variables || []).map(v => `{{${v}}}`).join(', ') || 'None'}`,
        overridden: 'Overridden',
        zhTitle: 'Chinese title',
        zhContent: 'Chinese body',
        enTitle: 'English title',
        enContent: 'English body',
        save: 'Save notification templates',
        saved: 'Notification templates saved',
      },
      system: {
        title: 'System Maintenance',
        maintenanceMode: 'Maintenance mode',
        maintenanceModeDesc: 'When enabled, normal users cannot access the site; admins remain allowed',
        backupTitle: 'Data backup',
        generating: 'Generating...',
        downloadDatabase: 'Download database',
        exporting: 'Exporting...',
        exportLogs: 'Export logs',
        processing: 'Processing...',
        clearCache: 'Clear cache',
        backupGenerated: 'Data backup generated',
        logsExported: 'Audit logs exported',
        cacheRefreshed: 'Cache refreshed',
        dangerTitle: '⚠️ Dangerous operation',
        dangerDescription: 'Production does not provide one-click database clearing in the page. To reset data, download a backup first, then have an administrator run a controlled script on the server.',
        saveConfig: 'Save configuration',
      },
    },
  },
};

const AdminLanguageContext = createContext(null);

export function normalizeAdminLanguage(value) {
  return SUPPORTED_ADMIN_LANGUAGES.includes(value) ? value : 'zh';
}

function readStoredLanguage() {
  if (typeof window === 'undefined' || !window.localStorage) return 'zh';
  return normalizeAdminLanguage(window.localStorage.getItem(ADMIN_LANGUAGE_STORAGE_KEY));
}

export function AdminLanguageProvider({ children }) {
  const [language, setLanguageState] = useState(readStoredLanguage);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(ADMIN_LANGUAGE_STORAGE_KEY, language);
    }
    if (typeof document !== 'undefined') {
      document.documentElement.lang = language === 'zh' ? 'zh-CN' : 'en';
    }
  }, [language]);

  const value = useMemo(() => ({
    language,
    copy: adminTranslations[language],
    setLanguage: (nextLanguage) => setLanguageState(normalizeAdminLanguage(nextLanguage)),
  }), [language]);

  return (
    <AdminLanguageContext.Provider value={value}>
      {children}
    </AdminLanguageContext.Provider>
  );
}

export function useAdminLanguage() {
  const context = useContext(AdminLanguageContext);
  if (!context) {
    return {
      language: 'zh',
      copy: adminTranslations.zh,
      setLanguage: () => {},
    };
  }
  return context;
}
